/**
 * Cognito Authentication Module
 * Handles federated login via Entra AD (Azure AD) through Cognito Hosted UI.
 * Generates JWT tokens and passes them with backend requests.
 */

const COGNITO_CONFIG = {
  dev: {
    userPoolId: "us-east-2_azRprYGkL",
    clientId: "2b6fbd1gmv5oiqp5aqc2rfn4dk", // Set after Cognito app client is created
    domain: "np-uat.auth.us-east-2.amazoncognito.com",
    redirectUri: window.location.origin,
    identityProvider: "np-orchestrator-uat",
  },
  prod: {
    userPoolId: "us-east-2_azRprYGkL",
    clientId: "2b6fbd1gmv5oiqp5aqc2rfn4dk", // Set after Cognito app client is created
    domain: "np-uat.auth.us-east-2.amazoncognito.com",
    redirectUri: window.location.origin,
    identityProvider: "np-orchestrator-uat",
  },
};

type Environment = "dev" | "prod";

function getEnvironment(): Environment {
  const hostname = window.location.hostname;
  if (hostname.includes("dev-")) return "dev";
  if (hostname === "localhost") return "dev";
  return "prod";
}

function getConfig() {
  return COGNITO_CONFIG[getEnvironment()];
}

// Token storage
let accessToken: string | null = null;
let idToken: string | null = null;
let refreshToken: string | null = null;
let tokenExpiry: number = 0;

export interface AuthUser {
  email: string;
  name: string;
  groups: string[];
}

/**
 * Initialize authentication - check for tokens in URL hash or localStorage
 */
export function initAuth(): boolean {
  // Check URL hash for callback tokens
  const hash = window.location.hash;
  if (hash && hash.includes("id_token")) {
    parseTokensFromHash(hash);
    // Clean URL
    window.history.replaceState(null, "", window.location.pathname);
    return true;
  }

  // Check localStorage for existing session
  const stored = localStorage.getItem("grdmgmt_tokens");
  if (stored) {
    try {
      const tokens = JSON.parse(stored);
      if (tokens.expiry > Date.now()) {
        accessToken = tokens.accessToken;
        idToken = tokens.idToken;
        refreshToken = tokens.refreshToken;
        tokenExpiry = tokens.expiry;
        return true;
      }
    } catch {
      localStorage.removeItem("grdmgmt_tokens");
    }
  }

  return false;
}

/**
 * Redirect user to Cognito Hosted UI for federated login
 */
export function login(): void {
  const config = getConfig();
  const authUrl =
    `https://${config.domain}/oauth2/authorize?` +
    `response_type=token&` +
    `client_id=${config.clientId}&` +
    `redirect_uri=${encodeURIComponent(config.redirectUri)}&` +
    `identity_provider=${config.identityProvider}&` +
    `scope=openid+email+profile`;

  window.location.href = authUrl;
}

/**
 * Log out and clear tokens
 */
export function logout(): void {
  const config = getConfig();
  accessToken = null;
  idToken = null;
  refreshToken = null;
  tokenExpiry = 0;
  localStorage.removeItem("grdmgmt_tokens");

  const logoutUrl =
    `https://${config.domain}/logout?` +
    `client_id=${config.clientId}&` +
    `logout_uri=${encodeURIComponent(config.redirectUri)}`;

  window.location.href = logoutUrl;
}

/**
 * Get the current access token (for Authorization header)
 */
export function getAccessToken(): string | null {
  if (tokenExpiry > 0 && Date.now() >= tokenExpiry) {
    // Token expired
    localStorage.removeItem("grdmgmt_tokens");
    return null;
  }
  return accessToken;
}

function getAuthorizationToken(): string | null {
  if (tokenExpiry > 0 && Date.now() >= tokenExpiry) {
    localStorage.removeItem("grdmgmt_tokens");
    return null;
  }
  return idToken ?? accessToken;
}

/**
 * Get current user info from the ID token
 */
export function getCurrentUser(): AuthUser | null {
  if (!idToken) return null;
  try {
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    return {
      email: payload.email || "",
      name: payload.name || payload.email || "",
      groups: payload["cognito:groups"] || [],
    };
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!accessToken && tokenExpiry > Date.now();
}

function parseTokensFromHash(hash: string): void {
  const params = new URLSearchParams(hash.substring(1));
  accessToken = params.get("access_token");
  idToken = params.get("id_token");
  const expiresIn = parseInt(params.get("expires_in") || "3600", 10);
  tokenExpiry = Date.now() + expiresIn * 1000;

  // Persist tokens
  localStorage.setItem(
    "grdmgmt_tokens",
    JSON.stringify({
      accessToken,
      idToken,
      refreshToken,
      expiry: tokenExpiry,
    })
  );
}

/**
 * Inject auth token into the sprout-store global config.
 * Call this after successful authentication.
 */
export function configureStoreAuth(): void {
  const token = getAuthorizationToken();
  if (token) {
    (window as any).__SPROUT_STORE_TOKEN__ = token;
    (window as any).__SPROUT_APP_ID__ = "ground-mgmt";
    // In dev, proxy handles routing; in prod, use the real API domain
    const env = getEnvironment();
    if (env === "prod") {
      (window as any).__SPROUT_API_BASE__ = "https://grdmgmt-api.np.nutrien.com";
    } else if (window.location.hostname === "localhost") {
      (window as any).__SPROUT_API_BASE__ = "https://dev-grdmgmt-api.np.nutrien.com";
    } else {
      (window as any).__SPROUT_API_BASE__ = "https://dev-grdmgmt-api.np.nutrien.com";
    }
  }
}
